import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Scale, Calendar as CalendarIcon, LogOut, Bell, Settings, RefreshCw, Clock, CreditCard } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import api from '../utils/api';
import { toast } from 'sonner';
import { format } from 'date-fns';

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPaymentRequest, setSelectedPaymentRequest] = useState(null);
  const isRTL = language === 'ar' || language === 'ur';

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchAppointments();
    fetchNotifications();
    fetchPaymentRequests();
  }, [user, navigate]);

  const fetchAppointments = async () => {
    try {
      const response = await api.get('/appointments/user');
      setAppointments(response.data.appointments || []);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/notifications/pending');
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchPaymentRequests = async () => {
    try {
      const response = await api.get('/payment-requests/user');
      setPaymentRequests(response.data.payment_requests || []);
    } catch (error) {
      console.error('Error fetching payment requests:', error);
    }
  };

  const handleOpenPaymentModal = (request) => {
    setSelectedPaymentRequest(request);
    setPaymentModalOpen(true);
  };

  const createPayPalOrderForRequest = async () => {
    if (!selectedPaymentRequest) return;
    try {
      const response = await api.post(`/payment-requests/${selectedPaymentRequest.id}/pay`);
      return response.data.order_id;
    } catch (error) {
      toast.error('Errore creazione ordine');
      throw error;
    }
  };

  const onPayPalApproveRequest = async (data) => {
    if (!selectedPaymentRequest) return;
    try {
      await api.post('/payment-requests/capture', {
        order_id: data.orderID,
        request_id: selectedPaymentRequest.id
      });
      toast.success('Pagamento completato!');
      setPaymentModalOpen(false);
      setSelectedPaymentRequest(null);
      fetchPaymentRequests();
    } catch (error) {
      toast.error('Errore nel pagamento');
    }
  };

  const fetchAvailableSlots = async (date) => {
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await api.get(`/slots/available?date=${dateStr}`);
      setAvailableSlots(response.data.slots || []);
    } catch (error) {
      console.error('Error fetching slots:', error);
      setAvailableSlots([]);
    }
  };

  const handleOpenReschedule = (appointment) => {
    setSelectedAppointment(appointment);
    setRescheduleDate(new Date());
    setAvailableSlots([]);
    setRescheduleModalOpen(true);
  };

  const handleReschedule = async (newSlotId) => {
    if (!selectedAppointment) return;
    setRescheduleLoading(true);
    try {
      await api.put(`/appointments/${selectedAppointment.id}/reschedule`, {
        new_slot_id: newSlotId
      });
      toast.success('Appuntamento riprogrammato con successo!');
      setRescheduleModalOpen(false);
      setSelectedAppointment(null);
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella riprogrammazione');
    } finally {
      setRescheduleLoading(false);
    }
  };

  useEffect(() => {
    if (rescheduleModalOpen && rescheduleDate) {
      fetchAvailableSlots(rescheduleDate);
    }
  }, [rescheduleDate, rescheduleModalOpen]);

  const handleConfirmAppointment = async (appointmentId) => {
    try {
      await api.post(`/notifications/confirm/${appointmentId}`);
      toast.success(t('dashboard.confirmAppointment') + ' ✓');
      fetchAppointments();
      fetchNotifications();
    } catch (error) {
      toast.error('Errore nella conferma');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!user) return null;

  return (
    <div className={`min-h-screen bg-muted ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <nav className="bg-primary text-white border-b border-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_legal-booking-hub/artifacts/evfhhmo1_logo%20studio%20legale_edited_edited.avif" 
                alt="Studio Legale"
                className="h-14 w-14 object-contain"
                style={{ imageRendering: 'crisp-edges' }}
              />
              <span className="text-xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                Studio Legale
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm hidden sm:inline">{user.first_name} {user.last_name}</span>
              {user.is_admin && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate('/admin')}
                  className="text-white hover:bg-white/10"
                  data-testid="admin-button"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Admin
                </Button>
              )}
              <LanguageSelector />
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-white hover:bg-white/10"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            {t('dashboard.welcome')}, {user.first_name}!
          </h1>
          <p className="text-muted-foreground">{t('dashboard.myAppointments')}</p>
        </div>

        {notifications.length > 0 && (
          <Card className="p-6 mb-8 border-warning bg-warning/5">
            <div className="flex items-start space-x-3">
              <Bell className="h-6 w-6 text-warning mt-1 animate-pulse" />
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-4">{t('dashboard.notifications')}</h3>
                {notifications.map((notif) => (
                  <div 
                    key={notif.id} 
                    className={`flex flex-col sm:flex-row justify-between items-start sm:items-center py-4 px-4 mb-3 rounded-lg border-l-4 ${
                      notif.urgency === 'high' 
                        ? 'bg-destructive/10 border-destructive' 
                        : notif.urgency === 'medium'
                        ? 'bg-warning/10 border-warning'
                        : 'bg-muted border-muted-foreground'
                    }`}
                  >
                    <div className="mb-3 sm:mb-0">
                      <p className="font-medium">{notif.message}</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {notif.appointment_date} alle {notif.appointment_time}
                      </p>
                      {notif.payment_pending && (
                        <p className="text-xs text-warning mt-1 font-medium">
                          Pagamento in sospeso
                        </p>
                      )}
                    </div>
                    <Button 
                      size="sm" 
                      onClick={() => handleConfirmAppointment(notif.appointment_id)}
                      className={`${
                        notif.urgency === 'high' 
                          ? 'bg-destructive hover:bg-destructive/90' 
                          : 'bg-success hover:bg-success/90'
                      }`}
                      data-testid={`confirm-notification-${notif.id}`}
                    >
                      {t('dashboard.confirmAppointment')}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* Payment Requests Section */}
        {paymentRequests.length > 0 && (
          <Card className="p-6 mb-8 border-secondary bg-secondary/5">
            <div className="flex items-start space-x-3">
              <CreditCard className="h-6 w-6 text-secondary mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-4">Richieste di Pagamento</h3>
                {paymentRequests.map((req) => (
                  <div 
                    key={req.id} 
                    className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-4 px-4 mb-3 rounded-lg border-l-4 bg-white border-secondary"
                  >
                    <div className="mb-3 sm:mb-0">
                      <p className="font-medium">{req.description}</p>
                      <p className="text-2xl font-bold text-primary mt-1">€{req.amount?.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Richiesto il {new Date(req.created_at).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                    <Button 
                      onClick={() => handleOpenPaymentModal(req)}
                      className="bg-secondary text-primary hover:bg-secondary/90"
                      data-testid={`pay-request-${req.id}`}
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      Paga Ora
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6 hover:shadow-md transition-shadow" data-testid="book-new-card">
            <CalendarIcon className="h-10 w-10 text-secondary mb-4" />
            <h3 className="text-xl font-semibold mb-2">{t('dashboard.bookNew')}</h3>
            <Button 
              onClick={() => navigate('/booking')}
              className="mt-4 w-full bg-secondary text-primary hover:bg-secondary/90"
              data-testid="book-new-button"
            >
              {t('booking.title')}
            </Button>
            {/* Legal disclaimer with red asterisk */}
            <p className="mt-4 text-xs text-destructive leading-relaxed">
              <span className="font-bold text-lg">*</span> Il costo della consulenza verrà detratto dalla parcella nel caso in cui l'avvocato riceverà il mandato dal cliente; diversamente, il pagamento della consulenza sarà trattenuto in quanto compenso per l'attività svolta dall'avvocato.
            </p>
          </Card>

          <Card className="p-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-2">{t('dashboard.upcoming')}</h3>
            <p className="text-3xl font-bold">
              {appointments.filter(a => a.status !== 'completed').length}
            </p>
          </Card>

          <Card className="p-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-2">{t('dashboard.completed')}</h3>
            <p className="text-3xl font-bold">
              {appointments.filter(a => a.status === 'completed').length}
            </p>
          </Card>
        </div>

        <Card className="p-6">
          <h2 className="text-2xl font-semibold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            {t('dashboard.myAppointments')}
          </h2>
          {loading ? (
            <p>Loading...</p>
          ) : appointments.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              Nessun appuntamento trovato
            </p>
          ) : (
            <div className="space-y-4">
              {appointments.map((appointment) => (
                <div
                  key={appointment.id}
                  className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 border rounded-sm hover:bg-muted/50 transition-colors gap-4"
                  data-testid={`appointment-${appointment.id}`}
                >
                  <div className="flex-1">
                    <p className="font-medium">
                      {appointment.date} - {appointment.start_time}
                    </p>
                    <div className="flex flex-wrap gap-3 mt-2 text-sm">
                      <span className={`px-2 py-1 rounded-sm ${
                        appointment.status === 'confirmed' ? 'bg-success/10 text-success' :
                        appointment.status === 'pending' ? 'bg-warning/10 text-warning' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {t(`dashboard.${appointment.status}`)}
                      </span>
                      <span className={`px-2 py-1 rounded-sm ${
                        appointment.payment_status === 'completed' ? 'bg-success/10 text-success' :
                        'bg-warning/10 text-warning'
                      }`}>
                        {t('dashboard.payment')}: {t(`dashboard.${appointment.payment_status}`)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {appointment.payment_status !== 'completed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenReschedule(appointment)}
                        className="flex items-center gap-2"
                        data-testid={`reschedule-btn-${appointment.id}`}
                      >
                        <RefreshCw className="h-4 w-4" />
                        Riprogramma
                      </Button>
                    )}
                    <div className="text-right">
                      <p className="font-semibold text-lg">€{appointment.payment_amount}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Reschedule Modal */}
        <Dialog open={rescheduleModalOpen} onOpenChange={setRescheduleModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
                Riprogramma Appuntamento
              </DialogTitle>
              <DialogDescription>
                Seleziona una nuova data e orario per il tuo appuntamento
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              <div>
                <h3 className="font-medium mb-3">Seleziona data</h3>
                <Calendar
                  mode="single"
                  selected={rescheduleDate}
                  onSelect={setRescheduleDate}
                  className="rounded-sm border"
                  data-testid="reschedule-calendar"
                />
              </div>
              
              <div>
                <h3 className="font-medium mb-3">Slot disponibili</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Data: <span className="font-semibold">{rescheduleDate ? format(rescheduleDate, 'dd/MM/yyyy') : '-'}</span>
                </p>
                
                {availableSlots.length === 0 ? (
                  <div className="text-center py-8 bg-muted/30 rounded-lg">
                    <Clock className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-muted-foreground font-medium">
                      Nessuno slot disponibile
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Prova un'altra data
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {availableSlots.map((slot) => (
                      <Button
                        key={slot.id}
                        onClick={() => handleReschedule(slot.id)}
                        disabled={rescheduleLoading}
                        className="w-full justify-between bg-white text-primary border-2 border-secondary hover:bg-secondary/10"
                        data-testid={`reschedule-slot-${slot.id}`}
                      >
                        <span className="font-medium">{slot.start_time} - {slot.end_time}</span>
                        <span className="text-success text-sm">Disponibile</span>
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setRescheduleModalOpen(false)}
              >
                Annulla
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Payment Request Modal */}
        <Dialog open={paymentModalOpen} onOpenChange={setPaymentModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
                Completa Pagamento
              </DialogTitle>
              <DialogDescription>
                Paga la richiesta tramite PayPal
              </DialogDescription>
            </DialogHeader>
            
            {selectedPaymentRequest && (
              <div className="mt-4">
                <div className="p-4 bg-muted rounded-lg mb-4">
                  <p className="text-sm text-muted-foreground">Descrizione</p>
                  <p className="font-medium">{selectedPaymentRequest.description}</p>
                  <p className="text-3xl font-bold mt-2 text-primary">€{selectedPaymentRequest.amount?.toFixed(2)}</p>
                </div>
                
                <PayPalScriptProvider options={{ 
                  "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID || 'test',
                  currency: 'EUR',
                  intent: 'capture',
                  components: 'buttons',
                  "enable-funding": 'paylater'
                }}>
                  <PayPalButtons
                    createOrder={createPayPalOrderForRequest}
                    onApprove={onPayPalApproveRequest}
                    style={{ layout: 'vertical' }}
                  />
                </PayPalScriptProvider>
                
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800 text-center">
                    <strong>Paga in 3 rate</strong> - Dividi il pagamento senza interessi
                  </p>
                </div>
              </div>
            )}
            
            <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setPaymentModalOpen(false);
                  setSelectedPaymentRequest(null);
                }}
              >
                Annulla
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default DashboardPage;
